import { Children, useId, useState } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import { buildLocalizedPath } from '../lib/localeSeoRoutes'
import { G2_PRIME_DOCUMENT_SIGN_URL, TRUSTPILOT_PRIME_DOCUMENT_SIGN_URL } from '../lib/trustListingUrls'
import { PublicPictureImg } from './PublicPictureImg'
import { TrustpilotReviewButton } from './trust/TrustpilotReviewButton'

/** Shown under Resources in the footer; full template hub, AU page, and blog stay in header nav only. */
const FOOTER_RESOURCES_PRIMARY = [
  ['/esignature', 'footer.linkEsignature'],
  ['/how-to-sign-documents-online', 'footer.linkHowSignOnline'],
  ['/how-it-works', 'footer.linkHowItWorksPage'],
  ['/esignature-for-freelancers', 'footer.linkEsignFreelancers'],
  ['/free-contract-templates', 'footer.linkFreeContractTemplates'],
  ['/secure-esignature', 'footer.linkSecureEsign'],
  ['/esignature/real-estate', 'footer.linkIndustryRealEstate'],
  ['/esignature/accountants', 'footer.linkIndustryAccountants'],
  ['/esignature/hr', 'footer.linkIndustryHr'],
  ['/esignature/agencies', 'footer.linkIndustryAgencies'],
  ['/free-esignature', 'footer.linkFreeEsign'],
]

const FOOTER_LINKS_INITIAL = 4

function FooterExpandableColumn({ title, expanded, onToggle, children }) {
  const { t } = useLanguage()
  const panelId = useId()
  const items = Children.toArray(children).filter((child) => child != null && child !== false)

  if (items.length <= FOOTER_LINKS_INITIAL) {
    return (
      <div className="footer-links-col">
        <h5>{title}</h5>
        {items}
      </div>
    )
  }

  const first = items.slice(0, FOOTER_LINKS_INITIAL)
  const rest = items.slice(FOOTER_LINKS_INITIAL)

  return (
    <div className="footer-links-col">
      <h5>{title}</h5>
      {first}
      <div id={panelId} className="footer-links-col__panel" hidden={!expanded}>
        {rest}
      </div>
      <button
        type="button"
        className="footer-links-col__toggle"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={onToggle}
      >
        {expanded ? t('footer.showLess') : t('footer.showMore')}
      </button>
    </div>
  )
}

/**
 * Shared Footer component used by:
 * - Landing page (auth, blog views)
 * - App view (document signing, email activity)
 * Ensures identical footer content and behavior across the site.
 */
export function Footer({ onNavigate, termsHref, privacyHref, onTermsClick, onPrivacyClick }) {
  const { t, language } = useLanguage()
  const [footerColExpanded, setFooterColExpanded] = useState({
    product: false,
    resources: false,
    company: false,
  })
  const lp = (logicalPath) => buildLocalizedPath(logicalPath, language)
  const scrollTo = (id) => {
    if (onNavigate) {
      onNavigate('auth')
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 100)
    }
  }

  return (
    <footer className="site-footer">
      <div className="footer-top">
        {/* Brand column */}
        <div className="footer-brand">
          <PublicPictureImg
            name="primedocumentsignlogo"
            alt="Prime Document Sign"
            className="footer-brand-logo-full"
            width={89}
            height={38}
            decoding="async"
            loading="lazy"
            fetchPriority="low"
          />
          <p className="footer-brand-tagline">{t('footer.tagline')}</p>
          <div className="footer-trust-chips">
            <span className="footer-trust-chip">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              AES-256 Encrypted
            </span>
            <span className="footer-trust-chip">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="20 6 9 17 4 12"/></svg>
              Legally Binding
            </span>
          </div>
          <div className="footer-trustpilot-cta">
            <TrustpilotReviewButton className="trustpilot-review-btn--footer" />
          </div>
        </div>

        {/* Nav columns */}
        <div className="footer-links">
          <FooterExpandableColumn
            title={t('footer.product')}
            expanded={footerColExpanded.product}
            onToggle={() =>
              setFooterColExpanded((prev) => ({ ...prev, product: !prev.product }))
            }
          >
            <button type="button" className="footer-link" onClick={() => scrollTo('how')}>
              {t('nav.howItWorks')}
            </button>
            <button type="button" className="footer-link" onClick={() => scrollTo('how')}>
              {t('footer.templates')}
            </button>
            <button type="button" className="footer-link" onClick={() => scrollTo('pricing')}>
              {t('nav.pricing')}
            </button>
            <a href={lp('/docusign-alternative')} className="footer-link">
              {t('footer.docusignAlt')}
            </a>
            <a href={lp('/docusign-vs-prime-document-sign')} className="footer-link">
              {t('footer.linkDocuSignVs')}
            </a>
            <a href={lp('/product')} className="footer-link">
              {t('footer.linkProduct')}
            </a>
            <a href={lp('/alternatives')} className="footer-link">
              {t('footer.linkAlternatives')}
            </a>
            <a href={lp('/what-is-esignature')} className="footer-link">
              {t('footer.linkWhatIsEsign')}
            </a>
          </FooterExpandableColumn>
          <FooterExpandableColumn
            title={t('footer.resources')}
            expanded={footerColExpanded.resources}
            onToggle={() =>
              setFooterColExpanded((prev) => ({ ...prev, resources: !prev.resources }))
            }
          >
            {FOOTER_RESOURCES_PRIMARY.map(([path, labelKey]) => (
              <a key={path} href={lp(path)} className="footer-link">
                {t(labelKey)}
              </a>
            ))}
          </FooterExpandableColumn>
          <FooterExpandableColumn
            title={t('footer.company')}
            expanded={footerColExpanded.company}
            onToggle={() =>
              setFooterColExpanded((prev) => ({ ...prev, company: !prev.company }))
            }
          >
            <a href={lp('/about')} className="footer-link">
              {t('nav.aboutUs')}
            </a>
            <a href={lp('/trust')} className="footer-link">
              {t('footer.linkTrust')}
            </a>
            <a href={lp('/reviews')} className="footer-link">
              {t('footer.linkReviews')}
            </a>
            <a href={lp('/why-primedocumentsign')} className="footer-link">
              {t('footer.linkWhyPrimeDocuSign')}
            </a>
            <a href={lp('/pricing')} className="footer-link">
              {t('footer.linkPricingPage')}
            </a>
            <a href={lp('/contact')} className="footer-link">
              {t('footer.linkContactPage')}
            </a>
            <a href={lp('/security')} className="footer-link">
              {t('footer.linkSecurity')}
            </a>
            <a href={lp('/esignature-legality')} className="footer-link">
              {t('footer.linkEsignLegality')}
            </a>
            <button type="button" className="footer-link" onClick={() => scrollTo('faq')}>
              {t('nav.faqs')}
            </button>
            <button type="button" className="footer-link" onClick={() => scrollTo('contact')}>
              {t('footer.contactSupport')}
            </button>
            <a href={G2_PRIME_DOCUMENT_SIGN_URL} target="_blank" rel="noopener noreferrer" className="footer-link">
              {t('footer.linkG2')}
            </a>
            <a href={TRUSTPILOT_PRIME_DOCUMENT_SIGN_URL} target="_blank" rel="noopener noreferrer" className="footer-link">
              {t('footer.linkTrustpilot')}
            </a>
          </FooterExpandableColumn>
          <div>
            <h5>Follow Us</h5>
            <div className="footer-social-icons">
              <a href="https://www.youtube.com/@primedocumentsignapp" target="_blank" rel="noopener noreferrer" className="footer-social-icon footer-social-icon--youtube" aria-label="YouTube">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://www.facebook.com/profile.php?id=61582265586381" target="_blank" rel="noopener noreferrer" className="footer-social-icon footer-social-icon--facebook" aria-label="Facebook">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/primedocumentsign/" target="_blank" rel="noopener noreferrer" className="footer-social-icon footer-social-icon--instagram" aria-label="Instagram">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <span>{t('footer.copyright')}</span>
        <div className="footer-legal">
          <a href={termsHref} className="footer-link" onClick={onTermsClick}>
            {t('footer.terms')}
          </a>
          <a href={privacyHref} className="footer-link" onClick={onPrivacyClick}>
            {t('footer.privacy')}
          </a>
        </div>
      </div>
    </footer>
  )
}
